import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Box, Button, Divider, Grid, TextField, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import { FaBan } from '@react-icons/all-files/fa/FaBan';
import { FaCalculator } from '@react-icons/all-files/fa/FaCalculator';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../common/IfComponent';
import { scrollableY } from '../../common/theme/scrollbar';
import { RootState } from '../../store';
import PricelistTree from '../pricelist/PricelistTree';
import { PricelistTreeItem } from '../pricelist/pricelistTypes';
import CalculationCanvas from './CalculationCanvas';
import CreateCalculationDialog from './dialogs/CreateCalculationDialog';
import CreateCostGroupDialog from './dialogs/CreateCostGroupDialog';
import { getCalculations } from './redux/calculationSelectors';
import {
    fetchCalculation,
    fetchCalculations,
    fetchCostTypes,
    fetchRounders,
    isEditingName,
    selectCalculationPricelist,
    showNewCalculationDialog,
    updateCalculation
} from './redux/calculationSlices';

const mapStateToProps = (state: RootState) => ({
    calculation: state.calculation.calculation,
    isCreateCostGroupDialogOpen: state.calculation.showHideCostGroupDialog,
    showCreateNewCalculationButton: state.calculation.showCreateNewCalculationButton,
    selectedPricelist: state.calculation.selectedCalculationPricelist,
    showEditNameInput: state.calculation.showEditNameInput,
    isEditNameLoading: state.calculation.isEditNameLoading,
    calculations: getCalculations(state)
});

type Props = ReturnType<typeof mapStateToProps>;

function Calculation({
    calculation,
    showCreateNewCalculationButton,
    selectedPricelist,
    showEditNameInput,
    isEditNameLoading,
    calculations
}: Props) {
    const dispatch = useDispatch();

    const [calculationState, setCalculationState] = useState({
        name: calculation.name,
        calculationId: calculation.calculationId,
        rounder: calculation.rounder
    });
    const [filter, setFilter] = useState('');

    const pricelistCalculationTypes = useMemo(() => {
        return Object.values(calculations).reduce((cp, cur) => {
            if (cur.pricelistId != null) {
                cp[cur.pricelistId] = true;
            }
            return cp;
        }, {} as { [pricelistId: number]: boolean });
    }, [calculations]);

    useEffect(() => {
        setCalculationState({
            name: calculation.name,
            calculationId: calculation.calculationId,
            rounder: calculation.rounder
        });
    }, [calculation]);

    useEffect(() => {
        dispatch(fetchCostTypes.request());
        dispatch(fetchRounders.request());
        dispatch(fetchCalculations.request());
    }, [dispatch]);

    const dispatchSelectPricelist = (pricelistId: number | number[]) => {
        dispatch(selectCalculationPricelist(Number(pricelistId)));
        dispatch(fetchCalculation.request(Number(pricelistId)));
    };

    const dispatchShowNewCalculationDialog = () => {
        dispatch(showNewCalculationDialog(true));
    };

    const showNameInput = (visible: boolean) => {
        dispatch(isEditingName(visible));
    };

    const handleNameChange = (calculationName: string) => {
        setCalculationState({
            name: calculationName,
            calculationId: calculation.calculationId,
            rounder: calculation.rounder
        });
    };

    const cancelEdit = () => {
        handleNameChange(calculation.name);
        showNameInput(false);
    };

    const handleUpdateCalculation = () => {
        dispatch(
            updateCalculation.request({
                calculationId: calculationState.calculationId,
                name: calculationState.name,
                rounder: calculationState.rounder
            })
        );
    };

    const getCalculationIcon = (pricelistId: number, parentId: number | undefined) => {
        if (pricelistCalculationTypes[pricelistId] != null) {
            return <FaCalculator title="Pricelist owns calculation" />;
        }
        if (parentId != null && pricelistCalculationTypes[parentId] != null) {
            return <FaCalculator title="Pricelist inherits calculation" color="#FFCC00" />;
        }

        return <FaBan title="No calculation on Pricelist" color="red" />;
    };

    const renderer = (plItem: PricelistTreeItem) => {
        return (
            <span>
                {plItem.displayName} {getCalculationIcon(plItem.id, plItem.parentId)}
            </span>
        );
    };
    return (
        <Grid container sx={{ flexFlow: 'row' }}>
            <Grid item xs={3} sx={theme => ({ ...scrollableY(theme) })}>
                <PricelistTree
                    multiSelect={false}
                    pricelistsSelected={dispatchSelectPricelist}
                    selectedPricelistIds={selectedPricelist}
                    filter={filter}
                    setFilter={setFilter}
                    treeItemRenderer={renderer}
                />
            </Grid>
            <Divider orientation="vertical" flexItem />
            <Grid item xs={9} sx={theme => ({ padding: theme.spacing(2) })}>
                {(showCreateNewCalculationButton ||
                    (calculation.inheritedFrom != null &&
                        calculation.inheritedFrom.inheritedFromPricelistId !== selectedPricelist)) && (
                    <Button color="primary" variant="contained" onClick={dispatchShowNewCalculationDialog}>
                        {' '}
                        Create calculation
                    </Button>
                )}
                {calculation.calculationId > 0 && (
                    <Box component="div">
                        <Typography
                            component="h3"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{ padding: '8px', marginBottom: '15px' }}
                        >
                            <TextField
                                autoFocus
                                margin="dense"
                                id="name"
                                label="Name"
                                variant="standard"
                                type="text"
                                fullWidth={false}
                                onChange={e => handleNameChange(e.target.value)}
                                value={calculationState.name}
                                disabled={!showEditNameInput || isEditNameLoading}
                            />
                            <If
                                condition={isEditNameLoading}
                                then={<LinearProgress sx={{ width: '182px' }} />}
                                else={null}
                            />

                            <If
                                condition={showEditNameInput && !isEditNameLoading}
                                then={
                                    <Box component="div">
                                        <Button onClick={() => handleUpdateCalculation()}>Save</Button>
                                        <Button onClick={() => cancelEdit()}>Cancel</Button>
                                    </Box>
                                }
                                else={null}
                            />
                            <If
                                condition={
                                    !showEditNameInput &&
                                    !isEditNameLoading &&
                                    !(
                                        calculation.inheritedFrom != null &&
                                        calculation.inheritedFrom.inheritedFromPricelistId !==
                                            selectedPricelist
                                    )
                                }
                                then={
                                    <IconButton
                                        sx={{ marginTop: '12px' }}
                                        onClick={() => showNameInput(!showEditNameInput)}
                                        aria-label="Change name"
                                    >
                                        <BorderColorIcon />
                                    </IconButton>
                                }
                                else={null}
                            />
                            {calculation.inheritedFrom != null &&
                                calculation.inheritedFrom?.inheritedFromPricelistId !== selectedPricelist && (
                                    <Box component="div">
                                        {' '}
                                        (Inherited from{' '}
                                        {calculation.inheritedFrom?.inheritedFromPricelistName})
                                    </Box>
                                )}
                        </Typography>
                        <CalculationCanvas />
                        <CreateCostGroupDialog />
                    </Box>
                )}
                <CreateCalculationDialog />
            </Grid>
        </Grid>
    );
}

export default connect(mapStateToProps)(Calculation);
